// web/src/data/offers.ts
import { auth, db } from "@/lib/firebase";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  increment,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
  where,
  type Timestamp,
} from "firebase/firestore";
import { httpsCallable, getFunctions } from "firebase/functions";
import { ensureConversation, sendOfferMessage } from "@/data/inbox";
import { sendOfferV2, type OfferType } from "./offersV2";

/* =========================
   Small helpers
========================= */

function requireUid(): string {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error("Sign in to continue.");
  return uid;
}

function convoId(a: string, b: string) {
  const [x, y] = [a, b].sort();
  return `${x}_${y}`;
}

/** Notify recipient (sender-created notifications under recipient). */
async function notify(
  toUid: string,
  payload: { type: string; text: string; fromUid?: string }
) {
  await setDoc(doc(collection(db, "users", toUid, "notifications")), {
    ...payload,
    fromUid: payload.fromUid ?? auth.currentUser?.uid ?? null,
    read: false,
    isRead: false,
    createdAt: serverTimestamp(),
  } as any);
}

/** Conversation preview bump (used by accept/decline/withdraw/complete). */
async function bumpConversation(
  convId: string,
  lastMessage: string,
  toUid: string,
  fromUid: string
) {
  await updateDoc(doc(db, "conversations", convId), {
    updatedAt: serverTimestamp(),
    lastMessage,
    lastMessageAt: serverTimestamp(),
    lastSenderId: fromUid,
    [`unreadCounts.${toUid}`]: increment(1),
    [`unreadCounts.${fromUid}`]: 0,
  } as any);
}

/** Top-level /messages entry (used by non-sendOffer flows). */
async function addMessage(params: {
  conversationId: string;
  kind: "text" | "offer";
  content: string;
  senderId: string;
  recipientId: string;
  offerId?: string | null;
}) {
  await addDoc(collection(db, "messages"), {
    conversationId: params.conversationId,
    kind: params.kind,
    content: params.content,
    offerId: params.offerId ?? null,
    senderId: params.senderId,
    recipientId: params.recipientId,
    createdAt: serverTimestamp(),
    isRead: false,
  } as any);
}

/* =========================
   Types
========================= */

export type SendOfferInput = {
  toUid: string;
  cardId?: string;
  cardOwnerUid?: string;
  cardName?: string;
  scryfall_id?: string;
  offerType: "purchase" | "trade";
  price?: number | null;
  message?: string | null;
};

export type OfferDoc = {
  id: string;
  fromUid: string;
  toUid: string;
  status: "open" | "accepted" | "declined" | "withdrawn" | "completed";
  offerType: OfferType;
  price: number | null;
  message: string | null;
  cardId: string | null;
  cardOwnerUid: string | null;
  cardName: string | null;
  scryfall_id: string | null;
  conversationId: string;
  createdAt?: Timestamp | null;
  updatedAt?: Timestamp | null;
  lastActorUid?: string | null;
};

/* ======================================================================
   CREATE (v2): canonical only. Mirrors handled by Cloud Functions.
   - ensureConversation (shared conv creation)
   - write canonical offer
   - best-effort: post an "offer" message to the thread
   - best-effort: notification to recipient
====================================================================== */

export async function sendOffer(input: SendOfferInput): Promise<string> {
  const me = auth.currentUser;
  if (!me) throw new Error("Must be signed in.");
  if (!input.toUid || input.toUid === me.uid) throw new Error("Invalid recipient.");

  const docData: any = {
    fromUid: me.uid,
    toUid: input.toUid,
    // mirror alt identity keys some existing code may rely on
    senderId: me.uid,
    recipientId: input.toUid,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    status: "pending",
    type: input.offerType, // purchase | trade (client semantic)
    message: input.message || null,
    lastMessage: input.message || null,
    lastMessageAt: serverTimestamp(),
    lastActorUid: me.uid,
    scryfall_id: input.scryfall_id || null,
    cardId: input.cardId || null,
    cardOwnerUid: input.cardOwnerUid || input.toUid,
    cardName: input.cardName || null,
    offeredPrice:
      input.offerType === "purchase" && typeof input.price === "number"
        ? Number(input.price)
        : null,
  };

  const ref = await addDoc(collection(db, "offers_v2"), docData);
  return ref.id;
}

/* ======================================================================
   ACCEPT / DECLINE (recipient updates top-level only)
====================================================================== */

export async function acceptOffer(offerId: string) {
  const me = requireUid();
  const ref = doc(db, "offers_v2", offerId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("Offer not found");
  const v = snap.data() as OfferDoc;

  if (v.toUid !== me) throw new Error("Only the recipient can accept this offer");
  if (v.status !== "open") return;

  await updateDoc(ref, {
    status: "accepted",
    updatedAt: serverTimestamp(),
    lastActorUid: me,
  } as any);

  const convId = v.conversationId || convoId(v.fromUid, v.toUid);
  const summary = "Offer accepted";

  await addMessage({
    conversationId: convId,
    kind: "text",
    content: summary,
    senderId: me,
    recipientId: v.fromUid,
  });
  await bumpConversation(convId, summary, v.fromUid, me);

  // Optional CF hook
  try {
    const fn = httpsCallable(getFunctions(), "transferOffer");
    await fn({ offerId });
  } catch {
    /* optional */
  }

  await notify(v.fromUid, {
    type: "offer_accepted",
    text: `Your offer${v.cardName ? ` on ${v.cardName}` : ""} was accepted.`,
    fromUid: me,
  });
}

export async function declineOffer(offerId: string) {
  const me = requireUid();
  const ref = doc(db, "offers_v2", offerId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("Offer not found");
  const v = snap.data() as OfferDoc;

  if (v.toUid !== me) throw new Error("Only the recipient can decline this offer");
  if (v.status !== "open") return;

  await updateDoc(ref, {
    status: "declined",
    updatedAt: serverTimestamp(),
    lastActorUid: me,
  } as any);

  const convId = v.conversationId || convoId(v.fromUid, v.toUid);
  const summary = "Offer declined";

  await addMessage({
    conversationId: convId,
    kind: "text",
    content: summary,
    senderId: me,
    recipientId: v.fromUid,
  });
  await bumpConversation(convId, summary, v.fromUid, me);

  await notify(v.fromUid, {
    type: "offer_declined",
    text: `Your offer${v.cardName ? ` on ${v.cardName}` : ""} was declined.`,
    fromUid: me,
  });
}

/* ======================================================================
   WITHDRAW / UPDATE NOTE (sender updates canonical only)
====================================================================== */

export async function withdrawOffer(offerId: string) {
  const me = requireUid();
  const ref = doc(db, "offers_v2", offerId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("Offer not found");
  const v = snap.data() as OfferDoc;

  if (v.fromUid !== me) throw new Error("Only the sender can withdraw this offer");
  if (v.status !== "open") throw new Error("Only open offers can be withdrawn");

  const now = serverTimestamp();
  await updateDoc(ref, { status: "withdrawn", updatedAt: now, lastActorUid: me } as any);

  const convId = v.conversationId || convoId(v.fromUid, v.toUid);
  const summary = "Offer withdrawn";

  await addMessage({
    conversationId: convId,
    kind: "text",
    content: summary,
    senderId: me,
    recipientId: v.toUid,
  });
  await bumpConversation(convId, summary, v.toUid, me);

  await notify(v.toUid, {
    type: "offer_withdrawn",
    text: `An offer${v.cardName ? ` for ${v.cardName}` : ""} was withdrawn.`,
    fromUid: me,
  });
}

export async function addOfferNote(offerId: string, note: string) {
  const me = requireUid();
  const ref = doc(db, "offers_v2", offerId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("Offer not found");
  const v = snap.data() as OfferDoc;

  if (v.fromUid !== me) throw new Error("Only the sender can update this offer");
  if (v.status !== "open") throw new Error("Only open offers can be updated");

  const clean = (note || "").trim();
  if (!clean) throw new Error("Note cannot be empty");
  const ts = new Date().toLocaleString();
  const prev = v.message || "";
  const next = prev ? `${prev}\n\n— Update (${ts}): ${clean}` : `Update (${ts}): ${clean}`;

  const now = serverTimestamp();
  await updateDoc(ref, { message: next, updatedAt: now, lastActorUid: me } as any);

  const convId = v.conversationId || convoId(v.fromUid, v.toUid);
  await addMessage({
    conversationId: convId,
    kind: "text",
    content: "Offer updated",
    senderId: me,
    recipientId: v.toUid,
  });
  await bumpConversation(convId, "Offer updated", v.toUid, me);

  await notify(v.toUid, {
    type: "offer_updated",
    text: `Offer${v.cardName ? ` for ${v.cardName}` : ""} updated.`,
    fromUid: me,
  });
}

/* ======================================================================
   COMPLETE (either participant marks completed)
====================================================================== */

export async function completeOffer(offerId: string) {
  const me = requireUid();
  const ref = doc(db, "offers_v2", offerId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("Offer not found");
  const v = snap.data() as OfferDoc;

  const now = serverTimestamp();
  const convId = v.conversationId || convoId(v.fromUid, v.toUid);

  if (v.fromUid === me) {
    await updateDoc(ref, { status: "completed", updatedAt: now, lastActorUid: me } as any);

    await addMessage({
      conversationId: convId,
      kind: "text",
      content: "Offer completed",
      senderId: me,
      recipientId: v.toUid,
    });
    await bumpConversation(convId, "Offer completed", v.toUid, me);
  } else if (v.toUid === me) {
    await updateDoc(ref, {
      status: "completed",
      updatedAt: now,
      lastActorUid: me,
    } as any);
    await addMessage({
      conversationId: convId,
      kind: "text",
      content: "Offer completed",
      senderId: me,
      recipientId: v.fromUid,
    });
    await bumpConversation(convId, "Offer completed", v.fromUid, me);
  } else {
    throw new Error("Not authorized");
  }
}

/* ======================================================================
   Convenience queries (canonical)
====================================================================== */

export async function listMyOffers(role: "sent" | "received", limitCount = 20) {
  const me = requireUid();
  const field = role === "sent" ? "fromUid" : "toUid";
  const col = collection(db, "offers_v2");
  const q = query(col, where(field, "==", me), orderBy("updatedAt", "desc"), limit(limitCount));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as OfferDoc) }));
}

export async function getOffer(offerId: string): Promise<OfferDoc | null> {
  const snap = await getDoc(doc(db, "offers_v2", offerId));
  return snap.exists() ? (snap.data() as OfferDoc) : null;
}

/* ======================================================================
   Legacy sendOffer (v1) wrapper
====================================================================== */

type LegacyInput = {
  toUid: string;
  offerType?: OfferType;
  price?: number | null;
  message?: string | null;
  cardId?: string | null;
  cardOwnerUid?: string | null;
  cardName?: string | null;
  scryfall_id?: string | null;
};

export async function sendLegacyOffer(input: LegacyInput) {
  const offerType: OfferType =
    input.offerType ?? (typeof input.price === "number" && isFinite(input.price) ? "purchase" : "trade");

  return sendOfferV2({
    toUid: input.toUid,
    offerType,
    offeredPrice: offerType === "purchase" ? (input.price ?? null) : null,
    message: input.message ?? null,
    cardId: input.cardId ?? null,
    cardOwnerUid: input.cardOwnerUid ?? null,
    scryfall_id: input.scryfall_id ?? null,
  });
}

